import { Heap } from 'heap-js';
import { Frustum, Vector3 } from 'three';

import { CopcOctree, CopcOctreeNode } from './copc-octree';

/** The camera as the octree sees it, in the point cloud's own coordinates. */
export interface CopcView {
  frustum: Frustum;
  cameraPosition: Vector3;
  orthographic: boolean;
  /** projectionMatrix[1][1]: cot(fov / 2), or 2 / view height for an orthographic camera. */
  focalLength: number;
  /** The canvas height in CSS pixels, which maxSpacingPixels is in. */
  screenHeight: number;
}

export interface CopcSelectionOptions {
  /** Most points drawn at once. */
  pointBudget: number;
  /** A node's children are drawn once the distance between its points is more pixels than this. */
  maxSpacingPixels: number;
}

/** How much finer than maxSpacingPixels a node's points get before its drawn children go. */
const KEEP_DRAWN_FACTOR = 0.75;

export interface CopcSelection {
  /** The nodes to draw, the ones that matter most on screen first. */
  nodes: CopcOctreeNode[];
  /** Hierarchy pages that traversal reached, which need loading before it can go deeper. */
  unloadedPages: string[];
}

/**
 * How many pixels apart the node's points are on screen, where its box is closest to the camera.
 * The box is cut to the points' bounds, so a flat point cloud's nodes aren't refined for points
 * that would be above or below it.
 */
export function projectedSpacing(node: CopcOctreeNode, view: CopcView): number {
  const pixels = node.spacing * view.focalLength * view.screenHeight * 0.5;
  if (view.orthographic) {
    return pixels;
  }

  const distance = node.box.distanceToPoint(view.cameraPosition);
  return distance <= 0 ? Infinity : pixels / distance;
}

/**
 * Picks the nodes to draw. A COPC's levels add up: a node holds a sample of its cube and its
 * children the points in between, so a node is drawn with all its ancestors. Nodes are visited
 * by how far apart their points are on screen, so the coarse levels near the camera come first and
 * the point budget cuts off the details far away.
 *
 * @param drawn The keys of the nodes drawn last frame.
 */
export function selectCopcNodes(
  octree: CopcOctree,
  view: CopcView,
  { pointBudget, maxSpacingPixels }: CopcSelectionOptions,
  drawn: ReadonlySet<string> = new Set(),
): CopcSelection {
  const nodes: CopcOctreeNode[] = [];
  const unloadedPages: string[] = [];
  const root = octree.root;
  if (!root) {
    return { nodes, unloadedPages };
  }

  const queue = new Heap<{ node: CopcOctreeNode; spacing: number }>(
    (a, b) => b.spacing - a.spacing,
  );
  queue.push({ node: root, spacing: projectedSpacing(root, view) });

  let pointCount = 0;
  while (queue.length > 0) {
    const { node, spacing } = queue.pop()!;
    if (!view.frustum.intersectsBox(node.box)) {
      continue;
    }

    const nodePoints = node.hierarchy.pointCount;
    if (pointCount + nodePoints > pointBudget && nodes.length > 0) {
      break;
    }
    pointCount += nodePoints;
    if (nodePoints > 0) {
      nodes.push(node);
    }

    // Drawn children stay until their parent is a bit finer than it takes to load them, so they
    // don't come and go as the camera moves back and forth around the threshold
    if (spacing <= maxSpacingPixels * KEEP_DRAWN_FACTOR) {
      continue;
    }
    const children = octree.children(node);
    const keepDrawnOnly = spacing <= maxSpacingPixels;
    if (!keepDrawnOnly) {
      unloadedPages.push(...children.unloadedPages);
    }
    for (const child of children.nodes) {
      if (keepDrawnOnly && !drawn.has(child.key)) {
        continue;
      }
      queue.push({ node: child, spacing: projectedSpacing(child, view) });
    }
  }

  return { nodes, unloadedPages };
}
